import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  defer,
  filter,
  finalize,
  from,
  ignoreElements,
  map,
  merge,
  type Observable,
  of,
  takeWhile,
  timeout,
} from 'rxjs';
import { WebSocketService } from '../websocket/websocket.service';
import { deriveArchiveId } from './archive-id.util';

export type ArchiveEvent =
  | { kind: 'progress'; percent: number; totalSize?: number }
  | { kind: 'completed'; downloadUrl: string }
  | { kind: 'failed'; reason?: string };

/**
 * The slice of a socket client the tracker needs. `R` = that client's room key (string union or enum), so the shared
 * WebSocketService and an app-local socket service both fit without a cast.
 */
export interface ArchiveSocket<R> {
  connect(): void;
  joinRoom(room: R, archiveId: string): Observable<{ eventType: string; data: unknown }>;
  leaveRoom(room: R, archiveId: string): void;
}

/** Silence, not build time: every progress report re-arms it — a large originals archive outlives five minutes. */
export const ARCHIVE_SILENCE_TIMEOUT_MS = 5 * 60 * 1000;

function toArchiveEvent({ eventType, data }: { eventType: string; data: unknown }): ArchiveEvent | null {
  const payload = data as { progress?: number; totalSize?: number; downloadUrl?: string; error?: string };
  if (eventType === 'imageArchiveProgress') {
    return { kind: 'progress', percent: payload.progress ?? 0, totalSize: payload.totalSize };
  }

  if (eventType === 'imageArchiveCompleted') return { kind: 'completed', downloadUrl: payload.downloadUrl ?? '' };
  if (eventType === 'imageArchiveError') return { kind: 'failed', reason: payload.error };
  return null;
}

/**
 * Joins the archive room (`room` = the client's ARCHIVE key → `archive:operations:<archiveId>`) BEFORE `start` runs
 * (a verdict that beats the join is lost), then streams progress until one terminal event. `start` failing or
 * `silenceMs` without an event = failed.
 */
export function trackArchive<R>(
  socket: ArchiveSocket<R>,
  room: R,
  archiveId: string,
  start?: () => Observable<unknown> | Promise<unknown>,
  silenceMs = ARCHIVE_SILENCE_TIMEOUT_MS,
): Observable<ArchiveEvent> {
  return defer(() => {
    socket.connect();
    const events$ = socket.joinRoom(room, archiveId).pipe(
      map(toArchiveEvent),
      filter((event): event is ArchiveEvent => event !== null),
    );
    const start$ = start
      ? defer(() => from(start())).pipe(
          ignoreElements(),
          catchError(() => of<ArchiveEvent>({ kind: 'failed', reason: 'start' })),
        )
      : of<never>();
    return merge(events$, start$).pipe(
      timeout({ each: silenceMs, with: () => of<ArchiveEvent>({ kind: 'failed', reason: 'timeout' }) }),
      takeWhile(event => event.kind === 'progress', true),
      finalize(() => socket.leaveRoom(room, archiveId)),
    );
  });
}

/**
 * Zip of the given files: archiveId derived from the ids, POST `/api/files/archive`, then the archive room's events.
 * Call in an injection context (constructor, field initializer, `runInInjectionContext`) — it `inject`s HttpClient
 * and WebSocketService. Lazy: nothing is sent until subscribed; unsubscribing leaves the room.
 */
export function startPhotoArchive({
  archiveName,
  fileIds,
}: {
  archiveName: string;
  fileIds: string[];
}): Observable<ArchiveEvent> {
  const http = inject(HttpClient);
  const socket = inject(WebSocketService);
  const { archiveId, sortedIds } = deriveArchiveId(fileIds);
  const post = (): Observable<unknown> => http.post('/api/files/archive', { archiveId, fileIds: sortedIds, archiveName });
  return trackArchive(socket, 'ARCHIVE', archiveId, post);
}
